// Galerie des portraits : fonctions pures sur la liste des fantômes photographiés.
// Persistée telle quelle en localStorage (voir services/storage.js), donc tolérante aux données douteuses.
import { GALLERY_LOCAL_LIMIT } from '../config/constants.js';
import { FAMILY_MEMBERS } from '../data/family.js';

const COLOR_RE = /^#[0-9a-f]{6}$/i;

/** @typedef {{ familyIndex:number, color:string, level:number, timestamp:number }} Portrait */

/** Nettoie une entrée lue depuis le stockage ; null si inexploitable. */
export function sanitizePortrait(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const familyIndex = Number(raw.familyIndex);
  const level = Number(raw.level);
  const timestamp = Number(raw.timestamp);
  if (!Number.isInteger(familyIndex) || familyIndex < 0) return null;
  return {
    familyIndex,
    color: COLOR_RE.test(String(raw.color)) ? String(raw.color).toLowerCase() : '#a855f7',
    level: Number.isInteger(level) && level >= 1 ? level : 1,
    timestamp: Number.isFinite(timestamp) && timestamp > 0 ? timestamp : 0,
  };
}

export function sanitizeGallery(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(sanitizePortrait).filter(Boolean).slice(0, GALLERY_LOCAL_LIMIT);
}

/** Identité du kami (indépendante du « tour » au-delà de la liste, voir data/family.js#familyName). */
function kamiId(familyIndex) {
  return familyIndex % FAMILY_MEMBERS.length;
}

/**
 * Ajoute un portrait en tête (le plus récent d'abord), borné en taille. Un seul portrait par kami :
 * une nouvelle capture du même kami (même à un tour ultérieur) remplace l'ancienne au lieu de s'y
 * ajouter. Ne mute pas la galerie d'origine.
 */
export function addPortrait(gallery, { familyIndex, color, level, timestamp = Date.now() }) {
  const portrait = sanitizePortrait({ familyIndex, color, level, timestamp });
  if (!portrait) return gallery;
  const withoutSameKami = gallery.filter((p) => kamiId(p.familyIndex) !== kamiId(portrait.familyIndex));
  return [portrait, ...withoutSameKami].slice(0, GALLERY_LOCAL_LIMIT);
}

/** Kamis (types, indice % taille de la famille) déjà connus d'après la galerie. */
export function knownTypes(gallery) {
  return new Set(gallery.map((p) => kamiId(p.familyIndex)));
}
